/**
 * The event catalogue (`docs/design/00-contracts.md` §11.5, principle 10; C-34, C-5).
 *
 * Events exist from day one: the domain service writes its business rows **and** the `event_log`
 * row in the same transaction, and listeners run after commit. Notifications are listeners on
 * events, never direct calls sprinkled through the code.
 *
 * Naming is `<aggregate>.<past_tense_verb>`, lower snake case, enforced in the database by
 * `ck_event_log__type_shape`. The aggregate prefix must name an aggregate the system actually has.
 *
 * Phase 0 ended at `policy.denied`. Phase 1 page 1 (Contacts) adds the four `contact.*` events
 * below; the role change on the Organization tab needs no new name, because
 * `organization.role_changed` already exists and already means exactly that.
 *
 * The three `tag.*` events came with the Contacts review: a tag is org data (principle 9), and
 * renaming or removing one changes what every contact list shows. `tag.deleted` carries the
 * number of contacts that lost the label, because that is the fact a notification — or an undo —
 * needs and the only fact the soft-deleted row itself no longer states.
 *
 * Phase 1 page 2 (Tasks) adds exactly the three the plan names — *"Hendelse publiseres ved
 * opprettelse/tildeling/fullføring"* — and not one more. There is no `task.updated` and no
 * `task.deleted`: `audit_log` already records both, nobody is notified by either, and an event
 * nothing listens to is a name to keep in step for no benefit.
 *
 * `task.assigned` covers assignment AND re-assignment, which is why it carries
 * `previousAssigneePersonId`. A task created with an assignee emits `task.created` and then
 * `task.assigned` caused by it, so the notification listener has exactly ONE subscription for
 * "you have been given work" instead of two rules that must agree.
 *
 * Phase 1 page 3 (Inbox) adds exactly ONE name, `message.posted`, because the plan names exactly
 * one notification — *"notifikasjoner ved nye meldinger"*. `thread.created` and
 * `thread.participant_added` stay unadded by the same rule that kept `task.updated` out:
 * `audit_log` already records both, nobody is notified by either, and a name nothing listens to
 * is a shape to keep in step for no benefit. Starting a thread posts its first message, so
 * "somebody has put a thread in front of you" already reaches its participants through
 * `message.posted`.
 *
 * `message.posted` carries `participantPersonIds` — the whole thread's live participants at the
 * moment of the post, the author included. That is the same rule as `actorName` above and for
 * the same reason: the listener runs after commit with no organization context and RLS would
 * (correctly) refuse it `thread_participants`. Carrying everyone and letting the listener remove
 * the author keeps the entire "who gets told" rule in the listener, where principle 10 puts it.
 *
 * KOI Del 1 adds four `task.*` names, and each is a notification the order names in as many
 * words: `task.mentioned` (@ i en kommentar), `task.commented`, `task.due_soon` (24 timer før) og
 * `task.overdue`. De to siste har ingen aktør — de publiseres av en planlagt jobb, ikke av noen
 * som gjorde noe — og det er den eneste formelle forskjellen fra de seks som fantes: `actorPersonId`
 * er nullbar på dem, og bare på dem.
 *
 * Still unadded, and adding one is adding a later page: `user.accepted_invite`,
 * `thread.created`, `thread.participant_added`, `grant.created`, `grant.revoked`.
 *
 * `contact.invited` rather than the `person.invited` this file once pencilled in: the three
 * `person.*` events are platform level and carry no organization, while an invitation is an
 * act of one organization on its own contact register and its subject is an `invitations` row.
 */
import { z } from 'zod';

import {
  grantPermissionSchema,
  messageSourceSchema,
  orgRoleSchema,
  taskPrioritySchema,
  taskStatusSchema,
  termsKindSchema,
} from '../enums.js';
import { CONTRACT_VERSION_KINDS } from '../contracts/contract-signing.js';
import { localeSchema } from '../locale.js';

const personCreated = z.object({
  personId: z.uuid(),
  email: z.email().nullable(),
  source: z.enum(['self_signup', 'invitation', 'contact', 'seed', 'import']),
});

const personMerged = z.object({
  winnerPersonId: z.uuid(),
  loserPersonId: z.uuid(),
  repointed: z.array(z.string()),
});

const personLookup = z.object({
  emailHash: z.string(),
  found: z.boolean(),
});

const userRegistered = z.object({
  userId: z.uuid(),
  personId: z.uuid(),
  email: z.email(),
});

const userLoggedIn = z.object({
  userId: z.uuid(),
  personId: z.uuid(),
  sessionId: z.string().nullable(),
  firstSession: z.boolean(),
});

const userLoggedOut = z.object({
  userId: z.uuid(),
  personId: z.uuid(),
  sessionId: z.string().nullable(),
});

const userSettingsUpdated = z.object({
  userId: z.uuid(),
  personId: z.uuid(),
  changedFields: z.array(z.string()),
  locale: localeSchema.optional(),
});

const consentRecorded = z.object({
  consentId: z.uuid(),
  personId: z.uuid(),
  organizationId: z.uuid().nullable(),
  kind: termsKindSchema,
  termsVersionId: z.uuid(),
  version: z.string(),
  actedAt: z.iso.datetime(),
});

const organizationCreated = z.object({
  organizationId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  createdByPersonId: z.uuid(),
});

const memberAdded = z.object({
  membershipId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  role: orgRoleSchema,
});

const roleChanged = z.object({
  membershipId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  fromRole: orgRoleSchema,
  toRole: orgRoleSchema,
});

/**
 * DE TRE DØRENE UT AV ET HUS, OG INN IGJEN (QA15 blokk B2).
 *
 * `personId` er det feltet som gjør dem nyttige: identitetsmellomlageret slår opp på den, og
 * `IdentityCacheListener` er nettopp derfor en lytter og ikke et kall inne i tjenesten
 * (prinsipp 10). Rollen følger med fordi en lytter ikke kan lese `org_memberships` — hen har
 * ingen organisasjonskontekst når hendelsen leveres.
 */
const membershipLifecycle = z.object({
  membershipId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  role: orgRoleSchema,
});

/** Fjerning bærer ett tall til: hvor mange åpne oppgaver som skiftet eier med personen. */
const memberRemoved = membershipLifecycle.extend({
  reassignedTaskCount: z.number().int().nonnegative(),
  /** Hvem de åpne oppgavene gikk til. Alltid den som utførte fjerningen — se members.service.ts. */
  reassignedToPersonId: z.uuid(),
});

/**
 * «Koble til en Tvitvi-bruker» (QA12 #8). To hendelser, og ingen av dem bærer hvem som ble spurt:
 * huset som spør skal ikke lære om adressen finnes (8b), og en lytter i det huset er huset.
 */
const contactLinkRequested = z.object({
  contactId: z.uuid(),
  organizationId: z.uuid(),
});

const personLinkAnswered = z.object({
  linkId: z.uuid(),
  /** Hvilket svar. `withdrawn` er når den spurte trakk en godtatt kobling tilbake. */
  outcome: z.enum(['accepted', 'declined', 'withdrawn']),
});

/**
 * DEN PERSONLIGE KOBLINGEN (253.16, 0934). Bare id-en: hvem som ble spurt, slår lytteren opp i basen
 * (`app.push_person_connection_notice`), så hendelsen ikke bærer personen den som spurte på e-post
 * ikke skal lære om finnes (8b).
 */
const personConnectionRequested = z.object({ connectionId: z.uuid() });
const personConnectionAnswered = z.object({
  connectionId: z.uuid(),
  outcome: z.enum(['accepted', 'declined', 'withdrawn']),
});

/** Det samme paret for et hus (9a). Heller ikke disse sier hvem forespørselen gikk til. */
const companyLinkRequested = z.object({
  companyId: z.uuid(),
  organizationId: z.uuid(),
});

const companyLinkAnswered = z.object({
  linkId: z.uuid(),
  outcome: z.enum(['accepted', 'declined', 'withdrawn']),
});

const contactCreated = z.object({
  contactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  displayName: z.string(),
  email: z.email().nullable(),
  /**
   * True when `app.resolve_person()` attached this contact to a person the platform already
   * knew — the acceptance criterion "samme person finnes bare én gang" in event form.
   */
  reusedPerson: z.boolean(),
  /** True when a soft-deleted contact held the slot and was restored instead (invariant I4). */
  restored: z.boolean(),
});

const contactUpdated = z.object({
  contactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  changedFields: z.array(z.string()),
});

const contactDeleted = z.object({
  contactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
});

/** 234.1 og F56: `mergedContactId` er slått inn i `contactId`, som blir stående. */
const contactMerged = z.object({
  contactId: z.uuid(),
  mergedContactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  mergedPersonId: z.uuid(),
});

/**
 * The firm register (vedtak 100.3). Three events, mirroring the three a contact has, because a
 * firm is a row of the same register and a listener that wants «hva skjedde i Kontakter i dag»
 * must not have to know that half of it publishes and half of it does not.
 */
const contactCompanyCreated = z.object({
  companyId: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  /** True when a soft-deleted firm with the same name was restored instead of a second created. */
  restored: z.boolean(),
});

const contactCompanyUpdated = z.object({
  companyId: z.uuid(),
  organizationId: z.uuid(),
  changedFields: z.array(z.string()),
  /**
   * How many contacts had their `company_name` copy rewritten by this rename. It is in the event
   * because it is the part of the change that happened somewhere else: a listener reading only
   * `changedFields` would see «name» and miss that eleven other rows moved with it.
   */
  renamedContacts: z.number().int().nonnegative(),
});

const contactCompanyDeleted = z.object({
  companyId: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
});

/*
 * KONTAKTTYPEN FINNES IKKE LENGER (QA12 #13/K-2).
 *
 * Tre hendelser sto her, speilet av taggfamilien ved siden av. Atle: «Typen bør følge det byrået
 * eller personen selv har satt.» Migrering 0259 gjorde hver type SOM VAR I BRUK til en tagg, og
 * 0292 slettet tabellen. Taggene publiserer fortsatt sine tre.
 */

const contactInvited = z.object({
  invitationId: z.uuid(),
  contactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  email: z.email(),
  /* Sak 232 (P2, 0460): null for en artist- eller ekstern invitasjon — de gir ikke et medlemskap. */
  role: orgRoleSchema.nullable(),
  kind: z.enum(['member', 'artist', 'external']).optional(),
  expiresAt: z.iso.datetime(),
  /* Jobb 3 (0495): invitasjonen skal på SMS også, til personens mobilnummer. */
  sms: z.boolean().optional(),
});

/**
 * Å trekke en invitasjon tilbake er en sikkerhetshandling, ikke en opprydding: den lenken ligger
 * i en innboks et sted, og øyeblikket den slutter å virke er et faktum noen kommer til å spørre om.
 */
const invitationRevoked = z.object({
  invitationId: z.uuid(),
  contactId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  email: z.email(),
});

const tagCreated = z.object({
  tagId: z.uuid(),
  organizationId: z.uuid(),
  key: z.string(),
  name: z.string(),
  /** True when a soft-deleted tag held the key and was restored instead — the tag's own I4. */
  restored: z.boolean(),
});

const tagUpdated = z.object({
  tagId: z.uuid(),
  organizationId: z.uuid(),
  changedFields: z.array(z.string()),
});

const tagDeleted = z.object({
  tagId: z.uuid(),
  organizationId: z.uuid(),
  key: z.string(),
  name: z.string(),
  /** Contacts that carried the tag when it was removed. The links are kept; this counts them. */
  contactCount: z.number().int().nonnegative(),
});

/**
 * The three `task.*` payloads share a spine, and every field of it is there because a listener
 * needs it **without a second query**: a listener runs after the request transaction has
 * committed, in a context that has no organization and therefore cannot read `tasks` at all.
 *
 * `actorName` in particular. A notification stores i18n parameters, never rendered text (§6.20),
 * and "Kari ga deg en oppgave" needs Kari's name as a parameter. Looking it up in the listener
 * would mean reading `contacts` with no org context, which RLS refuses — correctly. So the name
 * travels with the event, as a fact about the moment it happened.
 */
const taskCreated = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  assigneePersonId: z.uuid().nullable(),
  editionId: z.uuid().nullable(),
  departmentId: z.uuid().nullable(),
  dueAt: z.iso.datetime().nullable(),
  priority: taskPrioritySchema,
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

const taskAssigned = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  /** Never null: un-assigning publishes nothing, because it gives nobody any work. */
  assigneePersonId: z.uuid(),
  /** The person the work was taken from, when this is a re-assignment. */
  previousAssigneePersonId: z.uuid().nullable(),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * 219.4 (0401): ARTISTEN FULLFØRTE EN OPPGAVE BYRÅET GA HENNE, og den som ga den skal vite det.
 * Mottakeren står i hendelsen, som i bookingkjeden: lytteren kan ikke lese oppgaven selv.
 */
const taskGivenCompleted = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  /** Oppgavens oppretter — den som ga den. */
  givenByPersonId: z.uuid().nullable(),
  /** Ansvarlig, som allerede får «fullført» etter regel 4 når det ikke er artisten selv. */
  assigneePersonId: z.uuid().nullable(),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

const taskCompleted = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  /** Who was responsible for it. Null when it was completed while unassigned. */
  assigneePersonId: z.uuid().nullable(),
  completedByPersonId: z.uuid(),
  completedAt: z.iso.datetime(),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * THE DEADLINE MOVED — «Endring av ansvarlig, status eller frist utløser hendelse → notifikasjon»
 * (PAGES.md, Tasks [F1]). Assignment had an event and completion had one; the deadline had none.
 *
 * Both timestamps travel, because "flyttet til fredag" is a different sentence from "flyttet fra
 * fredag" and a listener that only had the new one could write neither honestly. Null on either
 * side is a real state: a deadline being ADDED and a deadline being REMOVED are both changes
 * somebody responsible for the work needs to know about.
 */
/**
 * THE STATUS MOVED, and it did NOT move to done — the last third of «endring av ansvarlig, status
 * eller frist utløser hendelse» (PAGES.md, Tasks [F1]).
 *
 * `task.completed` already exists and already covers the transition to `done`, with a payload of
 * its own (who closed it, and when) and a sentence of its own. Publishing both for that one
 * transition would notify the same person twice about one act, so this fires for every OTHER
 * transition and the two are disjoint by construction rather than by a rule a listener applies.
 *
 * Both statuses travel, because «satte til Blokkert» and «tok den ut av Blokkert» are different
 * sentences and a digest with only the new one could write neither.
 */
const taskStatusChanged = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  fromStatus: taskStatusSchema,
  toStatus: taskStatusSchema,
  /** Everyone responsible: the assignee and the medansvarlige. The listener drops the actor. */
  responsiblePersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

const taskRescheduled = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  previousDueAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  /** Everyone responsible: the assignee and the medansvarlige. The listener drops the actor. */
  responsiblePersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * SOMEBODY WAS PUT ON A TASK as a medansvarlig — the other half of the plan's «endring av
 * ansvarlig». `task.assigned` names the ONE person answerable; this names the people added beside
 * them, and it is a different sentence for a different act.
 *
 * ONLY THE ADDED. Removal publishes nothing, for the same reason re-assignment does not reach the
 * old assignee: losing work is not news anybody can act on, and a `limited` user just taken off a
 * task can no longer read it — so the notification would deep-link to a 403.
 */
const taskCoAssigned = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  addedPersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * NOEN BLE NEVNT (KOI Del 1, avgjørelse 13) — i beskrivelsen eller i en kommentar.
 *
 * ÉN HENDELSE FOR BEGGE STEDENE, fordi setningen er den samme: «Kari nevnte deg i Rigg scene».
 * Hvor navnet sto er en detalj lenken løser — panelet åpner på kommentaren når det er en.
 *
 * `mentionedPersonIds` er BARE DE NYE. En kommentar som redigeres og fortsatt nevner Kari skal
 * ikke varsle Kari igjen, og den regelen hører hjemme der forskjellen er kjent: i tjenesten, som
 * sammenligner mot `task_mentions` før den skriver.
 */
/**
 * NOEN BLE INVITERT TIL EN KALENDERHENDELSE (KOI Del 3).
 *
 * ÉN PERSON PER HENDELSE, ikke en liste. Varselet er personlig — «Siri inviterte deg til
 * Produksjonsmøte» — og `notifications` har én rad per mottaker uansett. En liste ville betydd at
 * lytteren brettet den ut, altså at formen ble brutt opp ett lag for sent.
 *
 * OG BARE DE NYE. Å rette klokkeslettet på et møte skal ikke invitere de samme fem en gang til;
 * tjenesten sammenlikner mot `calendar_event_attendees` før den publiserer.
 */
const calendarEventInvited = z.object({
  eventId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  title: z.string(),
  actorPersonId: z.uuid(),
  /**
   * Navnet på den som inviterte (QA5-B: varselet sa «inviterte deg til …» uten hvem). Valgfritt
   * fordi hendelser skrevet før feltet fantes ligger i loggen uten det.
   */
  actorName: z.string().optional(),
});

/**
 * 253.32: hendelsen er endret, og den som endret den svarte ja på «Skal deltakerne få beskjed?».
 * Én per deltaker (ikke den som endret), som invitasjonen.
 */
const calendarEventChanged = z.object({
  eventId: z.uuid(),
  organizationId: z.uuid(),
  personId: z.uuid(),
  title: z.string(),
  actorPersonId: z.uuid(),
  actorName: z.string().optional(),
});

/** 239.46: en deltaker svarte; arrangøren (den som laget hendelsen) skal få vite det. */
const calendarEventResponded = z.object({
  eventId: z.uuid(),
  organizationId: z.uuid(),
  organizerPersonId: z.uuid(),
  status: z.enum(['accepted', 'declined', 'tentative']),
  title: z.string(),
  actorPersonId: z.uuid(),
  actorName: z.string().optional(),
});

const taskMentioned = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  /** Kommentaren navnet sto i, eller null for oppgavens beskrivelse. */
  commentId: z.uuid().nullable(),
  mentionedPersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * NOEN KOMMENTERTE. Mottakerne er ansvarlig, medansvarlige og FØLGERNE — det er den ene
 * hendelsen der `task_watchers` gjør en forskjell, og hele grunnen til at tabellen finnes.
 *
 * `mentionedPersonIds` er med i nyttelasten selv om lytteren ikke skriver varsler for dem: de
 * har allerede fått `task.mentioned`, og den som ble nevnt skal ikke få to varsler om den samme
 * kommentaren. Lytteren trekker dem fra, som den trekker fra aktøren.
 */
const taskCommented = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  commentId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  mentionedPersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * FRISTEN NÆRMER SEG (24 timer) og FRISTEN ER UTLØPT — de to jobben publiserer.
 *
 * `actorPersonId` ER NULLBAR HER OG BARE HER. De åtte andre `task.*`-hendelsene er noe et menneske
 * gjorde; disse to er noe KLOKKA gjorde. Å finne på en aktør — systembrukeren, den som opprettet
 * oppgaven — ville gjort «Kari satte fristen til i går» av en hendelse ingen utløste.
 *
 * ÉN HENDELSE PER OPPGAVE PER TERSKEL, og det er jobben som garanterer det ved å se på
 * `notifications` for den terskelen før den publiserer. En hendelse som ble publisert på nytt hvert
 * kvarter ville vært det kvarteret varsellista sluttet å bli lest.
 */
const taskDueSoon = z.object({
  taskId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  dueAt: z.iso.datetime(),
  recipientPersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid().nullable(),
  actorName: z.string(),
});

/**
 * A message was posted in a thread — the Inbox's one event, and the one the plan asks for.
 *
 * Every field is here because a listener needs it without a second query, and a listener has no
 * organization context to make one with:
 *
 * * `participantPersonIds` is the whole live participation of the thread at the moment of the
 *   post, **including the author**. The listener removes the author; suppressing here instead
 *   would put half the notification rule in the service.
 * * `threadSubject` and `actorName` are the notification's i18n parameters (§6.20).
 * * `source` travels so a later digest can distinguish an internal message from an inbound
 *   email without reading the row. Everything this API writes is `internal`.
 */
const messagePosted = z.object({
  messageId: z.uuid(),
  threadId: z.uuid(),
  organizationId: z.uuid(),
  threadSubject: z.string(),
  source: messageSourceSchema,
  /** Null for a system message. Then nobody is the author and everybody is notified. */
  authorPersonId: z.uuid().nullable(),
  participantPersonIds: z.array(z.uuid()),
  /**
   * DE SAMME DELTAKERNE, MED HUSET HVER AV DEM SITTER I (QA14 #10, hull 4).
   *
   * Varselet ble skrevet i AVSENDERENS organisasjon. I en delt tråd sitter motpartens folk et
   * annet sted, og `app.push_notification` avviser da med 23503 «recipient is not a live contact
   * in organization». `NotificationWriter` svelger den med vilje — en avgang mellom to hus skal
   * ikke velte en melding — så utfallet var stille: ingen varsel, ingen feil, ingen logg.
   *
   * `.default([])` av den samme grunnen som over: en hendelse skrevet før feltet fantes spilles
   * av som «ingen seter kjent», og lytteren faller tilbake på avsenderens hus slik den gjorde før.
   *
   * `threadSubject` på setet er emnet slik DEN leseren ser tråden (sak 216.9, 0396): byråets direkte
   * tråd til en artist heter artistens navn hos byrået og byråets navn hos artisten. Uten feltet
   * gjelder `threadSubject` over.
   */
  participantSeats: z
    .array(
      z.object({
        personId: z.uuid(),
        organizationId: z.uuid(),
        threadSubject: z.string().optional(),
        /**
         * HVORFOR DENNE LESEREN FÅR VARSELET (0931, E1). Setene er FØLGERNE fra og med 0931 — ikke
         * alle med en deltakerrad — og grunnen står i varselet («Du er ansvarlig»). Valgfri, fordi
         * en eldre hendelse spilles av uten den.
         */
        reason: z
          .enum([
            'started',
            'replied',
            'mentioned',
            'responsible',
            'no_responsible',
            'added',
            'recipient',
            'manual',
          ])
          .optional(),
      }),
    )
    .default([]),
  /**
   * Who the message NAMES (`PAGES.md`, Inbox [F1]) — already narrowed to participants who are not
   * the author, by the service that wrote the rows.
   *
   * It travels in the payload as well as in `message_mentions` because a listener runs after the
   * request transaction has committed, with no organisation context: it could not read the table
   * if it wanted to, and RLS is right to refuse. The same reason `actorName` is here.
   *
   * `.default([])` rather than `.optional()`: every event written before mentions existed replays
   * as "nobody was named", which is true, and a listener never has to check for absence.
   */
  mentionedPersonIds: z.array(z.uuid()).default([]),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

const policyDenied = z.object({
  action: z.string(),
  reason: z.string(),
  personId: z.uuid(),
  resourceType: z.string(),
  resourceId: z.uuid().nullable(),
});

/**
 * SALENS SPESIFIKASJON ER ENDRET (SPILLESTED-KONSERT, «alltid siste versjon, med varsel»).
 *
 * `specVersion` er versjonen etter endringen (0408 øker den i basen). `changedFields` er nøklene i
 * `ROOM_SPEC_CHANGE_KEYS`: de faste feltene, salens navn og standard kveld, og `layouts` eller
 * `customFields` når et oppsett eller et eget felt ble endret. Advancen (agent F) lytter og
 * gjenåpner avvikene feltene berører.
 */
/**
 * KVELDEN ER LAGET (QA15-S grensesnitt 5). Sendes av den som lager kvelden (B: «Hvem opptrer?», og B2 når
 * kontrakten er signert). D lytter: `source: 'auto'` får salens standardmal når `templateId` er null. Malen
 * brukes én gang per kveld, så en kveld som alt har fått den, får den ikke igjen.
 */
const venueNightCreated = z.object({
  nightId: z.uuid(),
  roomId: z.uuid(),
  templateId: z.uuid().nullable(),
  source: z.enum(['manual', 'auto']),
});
const venueNightAutoCreated = z.object({
  nightId: z.uuid(),
  venueFestivalId: z.uuid(),
  houseOrganizationId: z.uuid(),
  counterpartyOrganizationId: z.uuid().nullable(),
  artistName: z.string(),
  nightOn: z.iso.date(),
  housePersonIds: z.array(z.uuid()),
  counterpartyPersonIds: z.array(z.uuid()),
});
const venueNightLeased = z.object({
  nightId: z.uuid(),
  concertId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  houseName: z.string(),
  roomName: z.string(),
  nightOn: z.iso.date(),
  recipientPersonIds: z.array(z.uuid()),
});
const arrangementChanged = z.object({
  anchor: z.enum(['venue_night', 'concert']),
  id: z.uuid(),
  title: z.string(),
  on: z.iso.date(),
  /** `arrangement.moved`: the day it was on (QA15-S K1). */
  from: z.iso.date().optional(),
  recipientPersonIds: z.array(z.uuid()),
  /** The parties outside the house — the agency, the artist and management (K1, 1270). */
  parties: z.array(z.object({ organizationId: z.uuid(), personId: z.uuid() })).optional(),
  houseName: z.string().optional(),
});
/**
 * FOLKENE PÅ VAKT (QA16 297.3). Lagt i `event_log` av følgefunksjonen (`app.arrangement_tells_its_crew`,
 * 1320), der vaktene faktisk avlyses, gjenopprettes og flyttes, så de varsles uansett hvilken vei
 * arrangementet ble endret. `retimed`: samme dag, nye tider.
 */
const arrangementCrewChanged = z.object({
  anchor: z.enum(['venue_night', 'concert']),
  id: z.uuid(),
  change: z.enum(['cancelled', 'reinstated', 'moved', 'retimed']),
  title: z.string(),
  on: z.iso.date(),
  from: z.iso.date().optional(),
  recipientPersonIds: z.array(z.uuid()),
});

/**
 * PÅMELDING OG BYTTE AV VAKTER (QA16 297.4). `opened`: en har meldt seg på en ledig vakt (til dem som
 * godkjenner) eller tilbyr en kollega vakta si (til kollegaen). `accepted`: kollegaen sa ja (til dem som
 * godkjenner). `decided`: godkjent eller avslått (til den som spurte og kollegaen). Mottakerne er regnet
 * i transaksjonen; tittel og tid står i payloaden fordi lytteren kjører uten organisasjon.
 */
const shiftRequestEvent = z.object({
  requestId: z.uuid(),
  shiftId: z.uuid(),
  kind: z.enum(['claim', 'swap']),
  status: z.enum(['pending_peer', 'pending', 'approved', 'declined', 'withdrawn']),
  title: z.string(),
  startsAt: z.iso.datetime(),
  personName: z.string(),
  peerName: z.string(),
  /** Avslått med en grunn: den som spurte, får se den (QA16 297.4). */
  reason: z.string().optional(),
  recipientPersonIds: z.array(z.uuid()),
});

const bookingDealOutside = z.object({
  performanceId: z.uuid(),
  agencyName: z.string(),
  artistName: z.string(),
  organizationName: z.string(),
});

const venueRoomSpecChanged = z.object({
  roomId: z.uuid(),
  specVersion: z.number().int(),
  changedFields: z.array(z.string()),
});

/** name -> { version, subjectType (the physical table), payload schema }. */
/**
 * BOOKINGKJEDEN KRYSSER TO HUS, OG DERFOR BÆRER HVER HENDELSE BEGGE (QA-punkt 10).
 *
 * ================================================================================================
 * Rapporten 11. september: «Byrået varsles ikke om ny forespørsel eller nytt bud. Festivalen
 * varsles ikke om svar, aksept eller at advancen er startet.»
 *
 * `organizationId` på selve hendelsen er huset den SKJEDDE i. `recipientOrganizationId` er huset
 * varselet skal lande i, og de to er med vilje ulike her: et bud sendes i festivalens hus og
 * varsles i byråets. `app.push_notification` er SECURITY DEFINER og krever bare at mottakeren er
 * en kontakt i huset varselet skrives i — porten finnes altså fra før, den var bare ikke brukt.
 *
 * ================================================================================================
 * MOTTAKEREN STÅR I HENDELSEN, ikke i lytteren.
 *
 * Hvem som skal vite noe er en avgjørelse tjenesten som utløste det har grunnlaget for: den
 * kjenner forespørselens ansvarlige og budets avsender. En lytter som skulle slått det opp måtte
 * lest på tvers av husgrensen en gang til, med en port som ikke finnes.
 */
const bookingEnquiryReceived = z.object({
  /**
   * Null for et nytt tilbud til en arrangør byrået ikke har en forespørsel fra (F21): da er
   * `bidId` det eneste varselet kan åpne, og mottakerne er reserven.
   */
  enquiryId: z.uuid().nullable(),
  /** Huset varselet skal skrives i — byråets. */
  recipientOrganizationId: z.uuid(),
  /** Null når ingen står som ansvarlig. Da skrives ingenting; innboksen bærer den likevel. */
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  /** `interest`, `bid` eller `offer` — teksten i varselet skiller dem. */
  kind: z.string(),
  /**
   * MOTTAKERENS EGEN BUDRAD, når det er den varselet skal åpne (sak 215.26): et tilbud fra byrået
   * lander som en rad i festivalens hus, og den er det festivalen kan lese. Valgfri: byråets
   * forespørsler åpnes på `enquiryId` som før.
   */
  bidId: z.uuid().nullable().optional(),
});

const bookingEnquiryAnswered = z.object({
  enquiryId: z.uuid(),
  /** Huset varselet skal skrives i — festivalens. */
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  /** `free`, `busy`, `hold` — eller `accepted` / `declined` når budet ble avgjort. */
  answer: z.string(),
  /**
   * FESTIVALENS BUD, som varselet åpner (QA5 E #2). Forespørselen er byråets rad og kan ikke leses
   * av festivalen. Valgfri: hendelser skrevet før feltet fantes har den ikke.
   */
  bidId: z.uuid().nullable().optional(),
});

/**
 * BYRÅET HAR DELT REISEFØLGET (sak 212). Arrangøren kan nå hente det inn på programposten, også når
 * avtalen ble gjort utenfor Tvitvi. Mottakeren er den som sendte budet, som i `answered`.
 */
const bookingEnquiryPartyShared = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  /** Festivalens bud, som varselet åpner. Forespørselen er byråets rad. */
  bidId: z.uuid().nullable(),
});

/**
 * BYRÅET ENDRET REISEFØLGET ETTER AT ADVANCEN BLE DELT (sak 222.45). Reisefølget følger budet inn i
 * advancen én gang (0429); deretter føres det i advancens Personer-fane. Arrangøren får vite at
 * lista hos byrået er endret, med navnene inn og ut. Mottakeren er den som sendte budet.
 */
const bookingEnquiryPartyChanged = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  bidId: z.uuid().nullable(),
  added: z.array(z.string()),
  removed: z.array(z.string()),
});

/**
 * EN GODTATT AVTALE ER ENDRET (sak 239.2, Atle 04.10: «kun varsling»). Arrangøren har satt et godtatt
 * bud eller tilbud til noe annet; byrået får vite det. Mottakeren står i hendelsen, som i resten av
 * bookingkjeden. `status` er budets nye status.
 */
const bookingAgreementChanged = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  status: z.string(),
});

/**
 * ARRANGØREN GODTOK MOTBUDET (F22, runde 4. oktober). Byrået la et annet tall på bordet, og
 * arrangøren sa ja til det. Byrået får vite det; mottakeren står i hendelsen, som ellers i kjeden.
 */
const bookingCounterAccepted = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
});

/**
 * ET FORSLAG TIL KONTRAKTEN (sak 222.4). Byrået foreslår, arrangøren godtar eller avslår — og den
 * andre parten får vite det. Mottakeren står i hendelsen, som i bookingkjeden: lytteren skal ikke
 * lese over husgrensen i en transaksjon som alt er lukket. `objectType`/`objectId` er det varselet
 * åpner i mottakerens hus.
 */
const contractProposalNotice = z.object({
  proposalId: z.uuid(),
  performanceId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  /** Huset som gjorde noe: byrået ved `sent`, arrangøren ved `decided`. */
  counterpartyName: z.string(),
  /** `accepted` eller `declined` ved `decided`; null ved `sent`. */
  decision: z.enum(['accepted', 'declined']).nullable(),
  objectType: z.enum(['concert', 'booking_bid', 'booking_enquiry']).nullable(),
  objectId: z.uuid().nullable(),
});

/**
 * Avtalearket (C3, sak 274). `accepted` er det C-kontrakt lytter på: `sign_now` lager
 * kontraktutkastet, `sign_later` lar saken stå med «Ingen kontrakt». Mottakeren står i hendelsen
 * av samme grunn som over: lytteren leser ikke over organisasjonsgrensen etter commit.
 */
const dealSheetAccepted = z.object({
  dealSheetId: z.uuid(),
  versionId: z.uuid(),
  mode: z.enum(['sign_now', 'sign_later']),
  /** Den som skrev versjonen som ble godtatt; null når motparten ikke er i Tvitvi. */
  recipientOrganizationId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
});

/**
 * A8 (279.3 a): DEN GODTATTE AVTALEN ER ENDRET FØR SIGNERING av én part, og endringen gjelder med en
 * gang. Den andre parten får varsel. Mottakeren og tingen varselet åpner i mottakerens hus står i
 * hendelsen: budet hos festivalen, forespørselen hos byrået.
 */
const dealSheetAmended = z.object({
  dealSheetId: z.uuid(),
  versionId: z.uuid(),
  versionNumber: z.number().int().positive(),
  recipientOrganizationId: z.uuid().nullable(),
  recipientPersonId: z.uuid().nullable(),
  objectType: z.enum(['booking_bid', 'booking_enquiry']).nullable(),
  objectId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  changedKeys: z.array(z.string()),
});

const dealSheetVersionProposed = z.object({
  dealSheetId: z.uuid(),
  versionId: z.uuid(),
  versionNumber: z.number().int().positive(),
  recipientOrganizationId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  changedKeys: z.array(z.string()),
});

/**
 * NOE SKJEDDE MED DEN FELLES KONTRAKTEN (C5, C7, C4.4). Én hendelse med `action`, så lytteren tegner
 * varselet og kortet ett sted. Mottakerne er regnet ut av tjenesten: husene som skal høre det, og
 * personene som venter på å signere.
 */
export const CONTRACT_ACTIVITY_ACTIONS = [
  'draft_created',
  'signed',
  'current',
  'change_requested',
  'signature_withdrawn',
  'external_registered',
  'external_confirmed',
  'external_disputed',
  'delegated',
  'change_proposed',
  'voided',
  'reminder',
  /* QA16 svar 310.10: the other party has no signer, or none with authority covering the amount. */
  'signer_missing',
  'signer_needs_authority',
] as const;
const contractActivity = z.object({
  contractId: z.uuid(),
  versionId: z.uuid(),
  number: z.string(),
  label: z.string(),
  action: z.enum(CONTRACT_ACTIVITY_ACTIONS),
  /**
   * Versjonens kind: «har signert» om annulleringen er ikke «har signert kontrakten» (klikkrunden
   * 27.10). Valgfri, så en hendelse uten den (påminnelsen) er gyldig som før.
   */
  versionKind: z.enum(CONTRACT_VERSION_KINDS).nullable().optional(),
  actorOrganizationName: z.string().nullable(),
  actorName: z.string().nullable(),
  recipientOrganizationIds: z.array(z.uuid()),
  recipientPersonIds: z.array(z.uuid()),
  comment: z.string().nullable(),
});

const sharedAdvanceOpened = z.object({
  advanceId: z.uuid(),
  /**
   * HENVENDELSEN ADVANCEN HENGER PÅ (vedtak 105.1).
   *
   * `object_type` har ingen `shared_advance`-verdi, og varselet skal uansett ta leseren til
   * engasjementet — det er der advancen åpnes fra. Å utvide enumen for en peker som fins fra
   * før ville vært en migrering for ingenting.
   */
  bookingEnquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  /**
   * INGEN ARTIST HER, og det er en skranke framfor et valg.
   *
   * Henvendelsen ligger i BYRÅETS hus, så festivalen — som ofte er den som åpner advancen — kan
   * ikke lese navnet gjennom RLS. Et felt som var utfylt halvparten av gangene ville gitt to
   * varianter av det samme varselet.
   *
   * Huset som startet den er lesbart for begge, og er dessuten det varselet handler om.
   */
  startedByName: z.string(),
});

/**
 * VAKTENE (201.14, 22. september). To hendelser, og hver er et varsel Atle navnga: «Personen får
 * beskjed når de settes på vakt eller vakten endres».
 *
 * `shift.assigned` bærer bare de som ble LAGT TIL, av samme grunn som `task.co_assigned`: å bli
 * tatt av en vakt er ikke noe man kan handle på, og en frivillig som ikke lenger står på vakta kan
 * ikke lese den. `shift.changed` bærer alle som står på vakta når TIDEN flytter seg — det er
 * endringen som endrer dagen deres. Aktøren er med på begge, og lytteren hopper over den.
 *
 * Tittel og tid står i payloaden fordi lytteren kjører etter commit uten organisasjonskontekst, og
 * RLS ville (riktig) nektet den å slå dem opp.
 */
const shiftAssigned = z.object({
  shiftId: z.uuid(),
  editionId: z.uuid(),
  title: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  addedPersonIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

const shiftChanged = z.object({
  shiftId: z.uuid(),
  editionId: z.uuid(),
  title: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  personIds: z.array(z.uuid()),
  actorPersonId: z.uuid(),
  actorName: z.string(),
});

/**
 * REGIONBESKYTTELSEN ER BRUTT (agent E, 0417). En konsert med samme artist ligger innenfor en
 * annen konserts radius og periode. Publiseres av en planlagt jobb (ingen aktør) som husker hvert
 * par og hver dato, så en ny eller flyttet konsert gir ett varsel og ikke ett per kvarter.
 * Mottakerne er medlemmene som har `concert_budget:read`: vilkåret er en del av avtalen.
 */
const concertRegionConflict = z.object({
  protectedConcertId: z.uuid(),
  otherConcertId: z.uuid(),
  artistName: z.string(),
  protectedStartsOn: z.iso.date(),
  otherStartsOn: z.iso.date(),
  otherPlace: z.string(),
  recipientPersonIds: z.array(z.uuid()),
});

/**
 * DATOFORESPØRSELEN (agent C, 0412–0414). Hendelsen skjer i det ene huset, varselet hører hjemme i
 * det andre, og bare definer-funksjonen kan lese mottakerne der — derfor står huset og menneskene
 * i nyttelasten, som i bookingkjeden.
 *
 * `venue_enquiry.*` går til huset: de med cella `venue_enquiry:respond`. `date_request.*` går til
 * arrangøren: den som sendte forespørselen og konsertens ansvarlige. Datoene er `YYYY-MM-DD`.
 *
 * `superseded` er «datoen er tatt»: en annen fikk den bekreftet, og holdet er sluppet.
 * `*_moved` skrives av triggerne i 0414 rett i `event_log` og leveres av `DateRequestSweeper`.
 */
const venueEnquiryNotice = z.object({
  enquiryId: z.uuid(),
  venueFestivalId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  requesterName: z.string(),
  artistName: z.string(),
  roomName: z.string(),
  requestedOn: z.string(),
});

const dateRequestNotice = z.object({
  requestId: z.uuid(),
  concertId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  venueName: z.string(),
  roomName: z.string(),
  artistName: z.string(),
  requestedOn: z.string(),
  holdRank: z.number().int().nullable(),
  holdUntil: z.string().nullable(),
  /**
   * «Datoen er tatt» (`date_request.superseded`): var det et hold som ble sluppet, eller en vanlig
   * forespørsel som ble avslått (222.32)? Varselet sier det ene eller det andre. Mangler den, er
   * det et hold, som før 222.32.
   */
  wasHold: z.boolean().optional(),
  /** QA16 funn 27: grunnen spillestedet ga for avslaget (`date_request.declined`), når det ga en. */
  reason: z.string().max(2000).optional(),
});

const dateRequestNightMoved = z.object({
  concertId: z.uuid(),
  nightId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  venueName: z.string(),
  artistName: z.string(),
  nightOn: z.string(),
  doorsAt: z.string().nullable(),
});

/** QA6-K4 H1 (0486): huset avlyste kvelden (`cancelled: true`) eller gjenopprettet den. */
const dateRequestNightCancelled = z.object({
  concertId: z.uuid(),
  nightId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  venueName: z.string(),
  roomName: z.string(),
  artistName: z.string(),
  nightOn: z.string(),
  cancelled: z.boolean(),
});

/**
 * 222.34: en ny melding i samtalen om en datoforespørsel. `fromName` er huset som skrev. Husets
 * mottakere har `venue_enquiry:respond`; arrangørens er den som sendte forespørselen. Aldri
 * forfatteren selv.
 */
/*
 * G2 (punkt 6): et kort om leiearket i samtalen om datoen varsles som en melding, med hva som skjedde
 * på arket (motbud eller godtatt), så varselet sier det og ikke «ny melding».
 */
const rentalDealEvent = z.enum(['proposed', 'accepted', 'declined', 'withdrawn']).optional();
const venueEnquiryMessagePosted = venueEnquiryNotice.extend({
  fromName: z.string(),
  dealEvent: rentalDealEvent,
});
const dateRequestMessagePosted = z.object({
  dealEvent: rentalDealEvent,
  requestId: z.uuid(),
  concertId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  venueName: z.string(),
  roomName: z.string(),
  requestedOn: z.string(),
  fromName: z.string(),
});

const venueEnquiryConcertMoved = z.object({
  nightId: z.uuid(),
  concertId: z.uuid(),
  venueFestivalId: z.uuid().nullable(),
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
  requesterName: z.string(),
  artistName: z.string(),
  nightOn: z.string(),
  doorsAt: z.string().nullable(),
});

/*
 * DELINGENE (sak 232, PLAN 1.4). P3 sender created/updated/revoked når organisasjonen deler,
 * endrer og trekker tilbake; P4s feier sender expired. Mottakeren varsles i sine egne hus (P4).
 * `objectType` er delingens type — programpost, ressurs, vakt, avdeling eller årgang.
 */
const shareObjectTypeSchema = z.enum([
  'performance',
  'resource',
  'shift',
  'department',
  'edition',
  /* F14 (0641): kjøreplanen for en scene, bare som lenke. */
  'stage',
  /* F12, F13 (0661). */
  'document',
  'folder',
  'edition_map',
  /* F18: spillestedets sal (spesifikasjonen) og arrangementet. */
  'venue_room',
  'venue_night',
  /* D4 (0936): artisten, delt med en ekstern manager. */
  'artist',
]);
const shareCreated = z.object({
  grantId: z.uuid(),
  organizationId: z.uuid(),
  objectType: shareObjectTypeSchema,
  objectId: z.uuid(),
  subjectPersonId: z.uuid(),
  kind: z.enum(['external', 'link']),
  permission: grantPermissionSchema,
  expiresAt: z.iso.datetime({ offset: true }).nullable(),
  grantedByPersonId: z.uuid(),
  contactPersonId: z.uuid().nullable(),
  /* F10: delingen er én av flere laget på én gang; tråden og varselet kommer fra `share.batch_created`. */
  batchId: z.uuid().optional(),
});
const shareUpdated = shareCreated.extend({ changed: z.array(z.string()) });
const shareRevoked = z.object({
  grantId: z.uuid(),
  objectType: shareObjectTypeSchema,
  objectId: z.uuid(),
  subjectPersonId: z.uuid(),
  revokedByPersonId: z.uuid(),
});
/* F10: flere objekter delt med den samme personen på én gang — én linje i tråden og ett varsel. */
const shareBatchCreated = z.object({
  batchId: z.uuid(),
  organizationId: z.uuid(),
  objectType: shareObjectTypeSchema,
  subjectPersonId: z.uuid(),
  kind: z.enum(['external', 'link']),
  grantIds: z.array(z.uuid()).min(1),
});
/*
 * F5: mottakeren åpnet delingen første gang, eller trykket «Bekreft mottatt» på lenkesiden. Den som
 * delte og kontaktpersonen varsles. Én hendelse per åpning: en lenkeside med fem ting er ett varsel.
 */
const shareReceipt = z.object({
  grantIds: z.array(z.uuid()).min(1),
  subjectPersonId: z.uuid(),
  kind: z.enum(['external', 'link']),
});
const shareExpired = z.object({
  grantId: z.uuid(),
  objectType: shareObjectTypeSchema,
  objectId: z.uuid(),
  subjectPersonId: z.uuid(),
  expiredAt: z.iso.datetime({ offset: true }),
});

/*
 * E-POSTEN (jobb 2, 0491). Tre meldinger hadde ingen hendelse å lytte på: lenken til en lenkeperson
 * (laget, eller laget på nytt — 232.2), «nytt hus» fra Control Room, og Supabase som ber oss sende
 * en e-post (glemt passord). LENKEN STÅR ALDRI I NYTTELASTEN: den følger hendelsen som en
 * hemmelighet til lytteren i transaksjonen (`EventBus.emit(..., { secrets })`) og skrives ikke i
 * event_log. `objectType`/`objectId` er det som ble delt da lenken ble laget — e-posten nevner det.
 */
const recipientLinkCreated = z.object({
  linkId: z.uuid(),
  organizationId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
  objectType: shareObjectTypeSchema.nullable(),
  objectId: z.uuid().nullable(),
  previousLinkId: z.uuid().nullable(),
  /* Jobb 3 (0495): lenken skal på SMS også, til personens mobilnummer. */
  sms: z.boolean().optional(),
});
/*
 * F34: en utsending er laget. Bare tall og id-er i loggen — mottakerne og teksten står i
 * `broadcast_recipients` og `broadcasts`, der lytteren leser dem i samme transaksjon.
 */
const broadcastSent = z.object({
  broadcastId: z.uuid(),
  festivalId: z.uuid(),
  editionId: z.uuid().nullable(),
  recipientCount: z.number().int().nonnegative(),
  includeShifts: z.boolean(),
});
const organizationInvitationCreated = z.object({
  invitationId: z.uuid(),
  email: z.email(),
  organizationName: z.string(),
  replacedId: z.uuid().nullable(),
  expiresAt: z.iso.datetime(),
});
/* Supabase sin `email_action_type`: recovery, signup, invite, magiclink, email_change, … */
const userAuthEmailRequested = z.object({
  authUserId: z.uuid(),
  actionType: z.string().regex(/^[a-z_]{1,40}$/),
});
/*
 * 284.5 (1151): personen ba om å slette kontoen. Lytteren sender e-posten med datoen og lenken for å
 * angre; adressen og navnet går som hemmeligheter, aldri i event_log.
 */
const userDeletionRequested = z.object({
  userId: z.uuid(),
  personId: z.uuid(),
  dueAt: z.iso.datetime(),
  locale: z.enum(['nb', 'en']),
});
/* «Angre»: kontoen er åpen igjen. Identitetscachen slippes, så sperren er borte ved neste kall. */
const userDeletionWithdrawn = z.object({ userId: z.uuid(), personId: z.uuid() });
/*
 * Jobb 4 (0493): virusskanningen stoppet en fil. Mottakerne — den som lastet opp og husets admin og
 * superadmin — regnes ut av `app.infected_attachment_recipients` når hendelsen publiseres, fordi
 * lytteren kjører uten organisasjon og RLS ikke ville sluppet den inn i medlemslista.
 */
const attachmentInfected = z.object({
  attachmentId: z.uuid(),
  filename: z.string(),
  signature: z.string().nullable(),
  detectedBy: z.enum(['upload', 'sweep']),
  recipientPersonIds: z.array(z.uuid()),
});

/*
 * EKSPORT (docs/EKSPORT.md §8): en stor eksport ble laget i bakgrunnen, eller ble ikke laget. Mottakeren
 * er den som bestilte den; `title` er dokumentets tittel i hens språk. Varselet lenker til «Mine eksporter».
 */
const exportFinished = z.object({
  jobId: z.uuid(),
  exportId: z.string(),
  title: z.string(),
  recipientPersonId: z.uuid(),
});

/* «Organisasjoner vi samarbeider med» (sak 239.21, 0541): skrevet av definer-funksjonene. */
/* Persontypene §3.2 (1096): leverandøren lagt inn, og de ansatte endret. */
const supplierCreated = z.object({
  supplierId: z.uuid(),
  organizationId: z.uuid(),
  editionId: z.uuid(),
  signupId: z.uuid(),
  /* Kontaktpersonen, som lenken til den personlige siden går til. */
  personId: z.uuid(),
  source: z.enum(['form', 'manual']),
  sendLink: z.boolean(),
});
const supplierLinkRequested = z.object({
  supplierId: z.uuid(),
  organizationId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
});
const supplierMembersChanged = z.object({
  supplierId: z.uuid(),
  organizationId: z.uuid(),
  editionId: z.uuid(),
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
});

const organizationPartnerEvent = z.object({
  counterpartyName: z.string(),
  /** Organisasjonen varselet skrives i — den andre parten, ikke den hendelsen hører til. */
  recipientOrganizationId: z.uuid(),
  recipientPersonIds: z.array(z.uuid()),
});

/**
 * F21 (runde 4. oktober, pakke 3): arrangøren trakk et bud byrået har fått. Mottakeren er den samme
 * som for et nytt bud — den ansvarlige, ellers reserven (`app.booking_notice_recipients`).
 */
const bookingBidWithdrawn = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
});

/** F21: artisten (med innlogging) er satt på reisefølget til en booking. Varselet går hjem til hen. */
const bookingPartyMemberAdded = z.object({
  personIds: z.array(z.uuid()),
  artistName: z.string(),
  agencyName: z.string(),
  /**
   * QA-runde 13, punkt 104: satt når lista ble fylt av triggeren fordi en NY konsert ble laget —
   * da er varselet «Ny konsert», ikke «satte deg på reisefølget». Uten feltet: en endring for hånd.
   */
  concert: z.object({ organiserName: z.string(), date: z.iso.date().optional() }).optional(),
});

/**
 * 245.5: arrangøren godtok eller avslo et bud byrået har fått. Mottakeren er den samme som for et nytt
 * bud — den ansvarlige, ellers reserven. `kind` skiller et bud fra et tilbud (215.26).
 */
const bookingBidAnswered = z.object({
  enquiryId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  counterpartyName: z.string(),
  kind: z.string().optional(),
});

/**
 * 253.80: et bud ble godtatt for et år som alt har en bekreftet pris, og prisen ble stående
 * (`app.accepted_bid_kept_price`, 0912). Beløpene går i øre; klienten skriver dem som penger.
 */
const bookingBidPriceKept = z.object({
  bidId: z.uuid(),
  recipientOrganizationId: z.uuid(),
  recipientPersonId: z.uuid().nullable(),
  artistName: z.string(),
  year: z.number().int(),
  bidAmountMinor: z.number().int(),
  bidCurrency: z.string(),
  confirmedAmountMinor: z.number().int(),
  confirmedCurrency: z.string(),
});

/**
 * A10 (0997): et meldt bud eller en forespørsel til et byrå som ikke er i Tvitvi. Plattformadminene
 * får e-post og ser det i Control Room › Logg; Tvitvi gir byrået beskjed.
 */
/** QA15-S E (1260): en datoforespørsel til en registersal som ikke er i Tvitvi — e-post til plattformadminene. */
const dateRequestUndeliverable = z.object({
  requestId: z.uuid(),
  venueName: z.string(),
  roomName: z.string(),
  requestedOn: z.string(),
  organizationName: z.string(),
});

const bookingBidUndeliverable = z.object({
  bidId: z.uuid(),
  agencyName: z.string(),
  artistName: z.string(),
  organizationName: z.string(),
});

/**
 * F59: plattformen frøs eller åpnet organisasjonen. 245.6: det som følger med til medlemmene, er
 * beskjeden til kunden — aldri den interne grunnen, som bare står i plattformloggen.
 */
const organizationFrozen = z.object({
  organizationId: z.uuid(),
  message: z.string().nullable().optional(),
});

/*
 * FRIVILLIGE (persontypene §3.4, §3.5, 1090). Påmeldt gjennom skjemaet (anonymt; «Varsle avdeling»
 * bærer lederne som skal varsles, fordi lytteren etter commit ikke kan slå dem opp), godkjent eller
 * avslått, og lagt til for hånd. E-postene er lyttere på disse.
 */
const volunteerSignedUp = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  formId: z.uuid().nullable(),
  personId: z.uuid(),
  personName: z.string(),
  notifyPersonIds: z.array(z.uuid()),
});
const volunteerDecided = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
  status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
});
/** «Send lenken» fra panelet (1090): den personlige lenken på e-post, uten at noe annet skjedde. */
const volunteerLinkRequested = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
});
const volunteerAdded = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
  sendLink: z.boolean(),
});
/* 1160 (290.5): avlyst av festivalen, og angret. E-posten om avlysningen er en lytter. */
const volunteerCancelled = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
  notify: z.boolean(),
  removedAssignmentIds: z.array(z.uuid()),
});
const volunteerReinstated = z.object({
  signupId: z.uuid(),
  editionId: z.uuid(),
  personId: z.uuid(),
  restoredAssignmentIds: z.array(z.uuid()),
});

/* 1080: artisttilgangen. `organizationId` er byrået, eller null når mottakeren ga videre. */
const artistAccessChanged = z.object({
  grantId: z.uuid(),
  organizationId: z.uuid().nullable(),
  artistId: z.uuid(),
  changedByPersonId: z.uuid(),
});

/*
 * 1145 (284.2): giveren (en manager) mistet tilgangen, og de hen la til, beholder sin — nå som
 * byråets egne delinger. Skrevet av basen (`app.artist_grant_detach_children`); mottakerne står i
 * hendelsen.
 */
const artistAccessGiverLost = z.object({
  agencyId: z.uuid(),
  artistId: z.uuid(),
  artistName: z.string(),
  giverPersonId: z.uuid(),
  giverName: z.string(),
  grantIds: z.array(z.uuid()),
  personIds: z.array(z.uuid()),
  personNames: z.array(z.string()),
  recipientPersonIds: z.array(z.uuid()),
});

/* 1106 (§2.3): bookingens chat er åpnet for reisefølget. Kan ikke skje to ganger for samme tråd. */
const bookingChatOpened = z.object({
  threadId: z.uuid(),
  agencyId: z.uuid(),
  bookingKind: z.enum(['enquiry', 'agency_booking']),
  bookingId: z.uuid(),
  openedByPersonId: z.uuid(),
});

/* 1120: hva artistsiden endret (`members`, `formats`, `documents`, `bookings`) og av hvem. */
const bookingArtistEditedByGrantee = z.object({
  artistId: z.uuid(),
  area: z.enum(['members', 'formats', 'documents', 'bookings']),
  changedByPersonId: z.uuid(),
});

export const EVENT_CATALOGUE = {
  'broadcast.sent': { version: 1, subjectType: 'broadcasts', schema: broadcastSent },
  'recipient_link.created': {
    version: 1,
    subjectType: 'recipient_links',
    schema: recipientLinkCreated,
  },
  'organization_invitation.created': {
    version: 1,
    subjectType: 'organization_invitations',
    schema: organizationInvitationCreated,
  },
  'user.auth_email_requested': { version: 1, subjectType: 'users', schema: userAuthEmailRequested },
  'user.deletion_requested': { version: 1, subjectType: 'users', schema: userDeletionRequested },
  'user.deletion_withdrawn': { version: 1, subjectType: 'users', schema: userDeletionWithdrawn },
  'share.created': { version: 1, subjectType: 'object_grants', schema: shareCreated },
  'share.updated': { version: 1, subjectType: 'object_grants', schema: shareUpdated },
  'share.revoked': { version: 1, subjectType: 'object_grants', schema: shareRevoked },
  'share.expired': { version: 1, subjectType: 'object_grants', schema: shareExpired },
  'share.batch_created': { version: 1, subjectType: 'object_grants', schema: shareBatchCreated },
  'share.opened': { version: 1, subjectType: 'object_grants', schema: shareReceipt },
  'share.confirmed': { version: 1, subjectType: 'object_grants', schema: shareReceipt },
  'concert.region_conflict': {
    version: 1,
    subjectType: 'concerts',
    schema: concertRegionConflict,
  },
  'shift.assigned': { version: 1, subjectType: 'shifts', schema: shiftAssigned },
  'shift.changed': { version: 1, subjectType: 'shifts', schema: shiftChanged },
  'person.created': { version: 1, subjectType: 'persons', schema: personCreated },
  'person.merged': { version: 1, subjectType: 'persons', schema: personMerged },
  'person.lookup': { version: 1, subjectType: 'persons', schema: personLookup },
  'user.registered': { version: 1, subjectType: 'users', schema: userRegistered },
  'user.logged_in': { version: 1, subjectType: 'users', schema: userLoggedIn },
  'user.logged_out': { version: 1, subjectType: 'users', schema: userLoggedOut },
  'user.settings_updated': { version: 1, subjectType: 'users', schema: userSettingsUpdated },
  'consent.recorded': { version: 1, subjectType: 'user_consents', schema: consentRecorded },
  'organization.created': { version: 1, subjectType: 'organizations', schema: organizationCreated },
  'organization.member_added': {
    version: 1,
    subjectType: 'org_memberships',
    schema: memberAdded,
  },
  'organization.role_changed': {
    version: 1,
    subjectType: 'org_memberships',
    schema: roleChanged,
  },
  'organization.frozen': { version: 1, subjectType: 'organizations', schema: organizationFrozen },
  'organization.unfrozen': { version: 1, subjectType: 'organizations', schema: organizationFrozen },
  'organization.member_deactivated': {
    version: 1,
    subjectType: 'org_memberships',
    schema: membershipLifecycle,
  },
  'organization.member_reactivated': {
    version: 1,
    subjectType: 'org_memberships',
    schema: membershipLifecycle,
  },
  'organization.member_removed': {
    version: 1,
    subjectType: 'org_memberships',
    schema: memberRemoved,
  },
  'contact.created': { version: 1, subjectType: 'contacts', schema: contactCreated },
  'contact.link_requested': {
    version: 1,
    subjectType: 'contacts',
    schema: contactLinkRequested,
  },
  'person_link.answered': { version: 1, subjectType: 'person_links', schema: personLinkAnswered },
  'person_connection.requested': {
    version: 1,
    subjectType: 'person_connections',
    schema: personConnectionRequested,
  },
  'person_connection.answered': {
    version: 1,
    subjectType: 'person_connections',
    schema: personConnectionAnswered,
  },
  'contact_company.link_requested': {
    version: 1,
    subjectType: 'contact_companies',
    schema: companyLinkRequested,
  },
  /* 217.3: huset som spurte, trakk forespørselen før noen svarte. */
  'contact_company.link_withdrawn': {
    version: 1,
    subjectType: 'contact_companies',
    schema: companyLinkRequested,
  },
  'company_link.answered': {
    version: 1,
    subjectType: 'company_links',
    schema: companyLinkAnswered,
  },
  'contact.updated': { version: 1, subjectType: 'contacts', schema: contactUpdated },
  'contact.deleted': { version: 1, subjectType: 'contacts', schema: contactDeleted },
  'contact.merged': { version: 1, subjectType: 'contacts', schema: contactMerged },
  'contact.invited': { version: 1, subjectType: 'invitations', schema: contactInvited },
  'invitation.revoked': { version: 1, subjectType: 'invitations', schema: invitationRevoked },
  'contact_company.created': {
    version: 1,
    subjectType: 'contact_companies',
    schema: contactCompanyCreated,
  },
  'contact_company.updated': {
    version: 1,
    subjectType: 'contact_companies',
    schema: contactCompanyUpdated,
  },
  'contact_company.deleted': {
    version: 1,
    subjectType: 'contact_companies',
    schema: contactCompanyDeleted,
  },
  'tag.created': { version: 1, subjectType: 'tags', schema: tagCreated },
  'tag.updated': { version: 1, subjectType: 'tags', schema: tagUpdated },
  'tag.deleted': { version: 1, subjectType: 'tags', schema: tagDeleted },
  'task.created': { version: 1, subjectType: 'tasks', schema: taskCreated },
  'task.assigned': { version: 1, subjectType: 'tasks', schema: taskAssigned },

  /* Bookingkjeden — se skjemaene over for hvorfor mottakeren står i hendelsen. */
  'booking_enquiry.received': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingEnquiryReceived,
  },
  'booking_enquiry.answered': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingEnquiryAnswered,
  },
  'booking_enquiry.party_shared': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingEnquiryPartyShared,
  },
  'shared_advance.opened': {
    version: 1,
    subjectType: 'shared_advances',
    schema: sharedAdvanceOpened,
  },
  /* Atles svar 29.09 (V2): reisefølget endret etter deling (222.45), og forslaget (222.4). */
  'booking_enquiry.party_changed': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingEnquiryPartyChanged,
  },
  /* Sak 239.2: en godtatt avtale som endres, gir varsel til motparten. */
  'booking_agreement.changed': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingAgreementChanged,
  },
  /* F22: arrangøren godtok byråets motbud. */
  'booking_counter.accepted': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingCounterAccepted,
  },
  'booking_bid.withdrawn': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingBidWithdrawn,
  },
  /* 245.5: arrangøren godtok eller avslo budet. */
  'booking_bid.accepted': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingBidAnswered,
  },
  'booking_bid.declined': {
    version: 1,
    subjectType: 'booking_enquiries',
    schema: bookingBidAnswered,
  },
  /* 253.80: et godtatt bud skrev ikke over årets bekreftede pris. */
  'booking_bid.price_kept': {
    version: 1,
    subjectType: 'booking_bids',
    schema: bookingBidPriceKept,
  },
  'booking_bid.undeliverable': {
    version: 1,
    subjectType: 'booking_bids',
    schema: bookingBidUndeliverable,
  },
  'booking_party.member_added': {
    version: 1,
    subjectType: 'booking_party_members',
    schema: bookingPartyMemberAdded,
  },
  'deal_sheet.accepted': {
    version: 1,
    subjectType: 'deal_sheets',
    schema: dealSheetAccepted,
  },
  'deal_sheet.version_proposed': {
    version: 1,
    subjectType: 'deal_sheets',
    schema: dealSheetVersionProposed,
  },
  'deal_sheet.amended': {
    version: 1,
    subjectType: 'deal_sheets',
    schema: dealSheetAmended,
  },
  'contract.activity': { version: 1, subjectType: 'contracts', schema: contractActivity },
  'contract_proposal.sent': {
    version: 1,
    subjectType: 'contract_proposals',
    schema: contractProposalNotice,
  },
  'contract_proposal.decided': {
    version: 1,
    subjectType: 'contract_proposals',
    schema: contractProposalNotice,
  },
  'task.completed': { version: 1, subjectType: 'tasks', schema: taskCompleted },
  'task.given_completed': { version: 1, subjectType: 'tasks', schema: taskGivenCompleted },
  'task.rescheduled': { version: 1, subjectType: 'tasks', schema: taskRescheduled },
  'task.status_changed': { version: 1, subjectType: 'tasks', schema: taskStatusChanged },
  'task.co_assigned': { version: 1, subjectType: 'tasks', schema: taskCoAssigned },
  // KOI Del 1. `task.overdue` shares `taskDueSoon`'s payload: both say "this deadline, these
  // people, no actor", and two identical schemas under two names is one shape to keep in step.
  // KOI Del 3. En invitasjon til en kalenderhendelse. `personId` er ÉN person og ikke en liste,
  // fordi varselet er personlig: fem inviterte er fem hendelser og fem rader, ikke én rad med fem
  // navn i seg — det er den samme formen `task.assigned` har, og den som lar `notifications` sin
  // `r_notifications_recipient` gjøre jobben sin.
  'event.invited': { version: 1, subjectType: 'calendar_events', schema: calendarEventInvited },
  'event.responded': { version: 1, subjectType: 'calendar_events', schema: calendarEventResponded },
  'event.changed': { version: 1, subjectType: 'calendar_events', schema: calendarEventChanged },
  'task.mentioned': { version: 1, subjectType: 'tasks', schema: taskMentioned },
  'task.commented': { version: 1, subjectType: 'tasks', schema: taskCommented },
  'task.due_soon': { version: 1, subjectType: 'tasks', schema: taskDueSoon },
  'task.overdue': { version: 1, subjectType: 'tasks', schema: taskDueSoon },
  'message.posted': { version: 1, subjectType: 'messages', schema: messagePosted },
  'policy.denied': { version: 1, subjectType: 'organizations', schema: policyDenied },
  'venue_night.created': { version: 1, subjectType: 'venue_nights', schema: venueNightCreated },
  'venue_night.auto_created': {
    version: 1,
    subjectType: 'venue_nights',
    schema: venueNightAutoCreated,
  },
  'venue_night.leased': { version: 1, subjectType: 'venue_nights', schema: venueNightLeased },
  'arrangement.cancelled': { version: 1, subjectType: 'venue_nights', schema: arrangementChanged },
  'arrangement.reinstated': { version: 1, subjectType: 'venue_nights', schema: arrangementChanged },
  'arrangement.moved': { version: 1, subjectType: 'venue_nights', schema: arrangementChanged },
  'arrangement.crew_changed': {
    version: 1,
    subjectType: 'venue_nights',
    schema: arrangementCrewChanged,
  },
  'shift_request.opened': { version: 1, subjectType: 'shift_requests', schema: shiftRequestEvent },
  'shift_request.accepted': { version: 1, subjectType: 'shift_requests', schema: shiftRequestEvent },
  'shift_request.decided': { version: 1, subjectType: 'shift_requests', schema: shiftRequestEvent },
  'booking_deal.outside_tvitvi': {
    version: 1,
    subjectType: 'performances',
    schema: bookingDealOutside,
  },
  'venue_room.spec_changed': {
    version: 1,
    subjectType: 'venue_rooms',
    schema: venueRoomSpecChanged,
  },
  // Datoforespørselen (agent C, 0412–0414). Se skjemaene over katalogen for hvem som mottar hva.
  'venue_enquiry.received': {
    version: 1,
    subjectType: 'venue_enquiries',
    schema: venueEnquiryNotice,
  },
  'venue_enquiry.withdrawn': {
    version: 1,
    subjectType: 'venue_enquiries',
    schema: venueEnquiryNotice,
  },
  'venue_enquiry.concert_moved': {
    version: 1,
    subjectType: 'venue_nights',
    schema: venueEnquiryConcertMoved,
  },
  'date_request.held': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.promoted': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.expired': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.confirmed': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.declined': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.released': { version: 1, subjectType: 'date_requests', schema: dateRequestNotice },
  'date_request.superseded': {
    version: 1,
    subjectType: 'date_requests',
    schema: dateRequestNotice,
  },
  'date_request.undeliverable': {
    version: 1,
    subjectType: 'date_requests',
    schema: dateRequestUndeliverable,
  },
  'date_request.night_moved': {
    version: 1,
    subjectType: 'concerts',
    schema: dateRequestNightMoved,
  },
  'date_request.night_cancelled': {
    version: 1,
    subjectType: 'concerts',
    schema: dateRequestNightCancelled,
  },
  // Atles svar 29.09 (agent V1, 0443): «Forleng hold» (224.5) og meldingene i samtalen (222.34).
  'date_request.hold_extended': {
    version: 1,
    subjectType: 'date_requests',
    schema: dateRequestNotice,
  },
  'date_request.message_posted': {
    version: 1,
    subjectType: 'date_requests',
    schema: dateRequestMessagePosted,
  },
  'venue_enquiry.message_posted': {
    version: 1,
    subjectType: 'venue_enquiries',
    schema: venueEnquiryMessagePosted,
  },
  /* Jobb 4 (0493): fila ble stoppet av virusskanningen — ved opplasting eller av feieren. */
  'attachment.infected': {
    version: 1,
    subjectType: 'attachments',
    schema: attachmentInfected,
  },
  /* docs/EKSPORT.md §8: den store eksporten er klar i «Mine eksporter», eller ble ikke laget. */
  'export.ready': { version: 1, subjectType: 'export_jobs', schema: exportFinished },
  'export.failed': { version: 1, subjectType: 'export_jobs', schema: exportFinished },
  /* Sak 239.21 (0541): forespørsel om samarbeid, og svaret på den. */
  'organization_partner.requested': {
    version: 1,
    subjectType: 'organization_links',
    schema: organizationPartnerEvent,
  },
  'organization_partner.accepted': {
    version: 1,
    subjectType: 'organization_links',
    schema: organizationPartnerEvent,
  },
  'organization_partner.declined': {
    version: 1,
    subjectType: 'organization_links',
    schema: organizationPartnerEvent,
  },
  'supplier.created': { version: 1, subjectType: 'suppliers', schema: supplierCreated },
  'supplier.link_requested': {
    version: 1,
    subjectType: 'suppliers',
    schema: supplierLinkRequested,
  },
  'supplier.members_changed': {
    version: 1,
    subjectType: 'suppliers',
    schema: supplierMembersChanged,
  },
  'volunteer.signed_up': {
    version: 1,
    subjectType: 'volunteer_signups',
    schema: volunteerSignedUp,
  },
  'volunteer.decided': { version: 1, subjectType: 'volunteer_signups', schema: volunteerDecided },
  'volunteer.added': { version: 1, subjectType: 'volunteer_signups', schema: volunteerAdded },
  'volunteer.link_requested': {
    version: 1,
    subjectType: 'volunteer_signups',
    schema: volunteerLinkRequested,
  },
  'volunteer.cancelled': {
    version: 1,
    subjectType: 'volunteer_signups',
    schema: volunteerCancelled,
  },
  'volunteer.reinstated': {
    version: 1,
    subjectType: 'volunteer_signups',
    schema: volunteerReinstated,
  },
  /* 1080 (persontypene §2.1): en artisttilgang er gitt, endret eller trukket — av byrået eller innenfor en annens. */
  'artist_access.changed': {
    version: 1,
    subjectType: 'object_grants',
    schema: artistAccessChanged,
  },
  'artist_access.giver_lost': {
    version: 1,
    subjectType: 'object_grants',
    schema: artistAccessGiverLost,
  },
  /* 1120 (sak 9): artistsiden med hele artisten + «Kan endre» endret byråets artist. */
  'booking_artist.edited_by_grantee': {
    version: 1,
    subjectType: 'booking_artists',
    schema: bookingArtistEditedByGrantee,
  },
  'booking_chat.opened': {
    version: 1,
    subjectType: 'booking_chats',
    schema: bookingChatOpened,
  },
} as const;

export type EventName = keyof typeof EVENT_CATALOGUE;

export type EventPayload<T extends EventName> = z.infer<(typeof EVENT_CATALOGUE)[T]['schema']>;

/** Every Phase 0 event name, for coverage tests and for `event_log` validation. */
export const EVENT_NAMES = Object.keys(EVENT_CATALOGUE) as readonly EventName[];

export const isEventName = (value: unknown): value is EventName =>
  typeof value === 'string' && Object.hasOwn(EVENT_CATALOGUE, value);

export const eventNameSchema: z.ZodType<EventName> = z
  .custom<EventName>(isEventName, { message: 'Unknown event name.' })
  .meta({ id: 'EventName' });

/**
 * Validates a payload against its own schema. The emitter calls this before buffering, so an
 * `event_log` row can never carry a shape a listener cannot read.
 */
export function parseEventPayload<T extends EventName>(name: T, payload: unknown): EventPayload<T> {
  return EVENT_CATALOGUE[name].schema.parse(payload) as EventPayload<T>;
}

/** `<aggregate>.<past_tense_verb>`, lower snake case — the shape `ck_event_log__type_shape` enforces. */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
